'use client'

import {
  type ButtonHTMLAttributes,
  Children,
  type CSSProperties,
  Fragment,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

/**
 * A form taken one question at a time — the shell the enquiry funnel lives in.
 *
 * It arrived as a vendored `.jsx` from a component gallery: untyped, wired to
 * `motion/react`, and styled against sky-blue fallbacks (`#0ea5e9`) that belong
 * to no palette here. Three things were wrong with that beyond the aesthetics.
 *
 * **Forty-six kilobytes for four transitions.** Lighthouse measured the whole
 * animation library downloaded and 99.9% of it never executed, on `/pl` and
 * `/pl/kontakt` both — the two pages the funnel is reachable from. Everything
 * it was used for is a height, a colour, a slide and a stroke, and each of
 * those is one CSS declaration. The rest of this repo already animates that
 * way (`bento-copy-card`, `flou-growth-chart`, `[data-reveal]`), so the library
 * was not even a consistent choice — it was a third motion system next to the
 * GSAP in the headline and the transitions everywhere else.
 *
 * **The step numbers were a `div` with an `onClick`.** No role, no tab stop, no
 * keyboard. They are now decoration and say so: the one call site already
 * passed `disableStepIndicators` because clicking them ROUTED AROUND the
 * validation on the Next button — you could land on the booking step with the
 * form empty. A control whose only correct configuration is "off" is not a
 * control, so the row is `aria-hidden` and a live region announces the step
 * instead.
 *
 * **Two steps could share the frame.** `AnimatePresence` crossfaded them over
 * each other, which on a form reads as a double exposure. They now hand over
 * the way the bento's slides do: the outgoing one leaves (200 ms), and only
 * then does the incoming one arrive.
 */

/** How long the outgoing step takes to clear the stage. */
const OUT_MS = 200

export function Step({ children }: { children: ReactNode }) {
  return <div className="step-default">{children}</div>
}

type StepperProps = {
  children: ReactNode
  /** Fired for every step that is not past the end. */
  onStepChange?: (step: number) => void
  /** Fired instead when the last step is completed. */
  onFinalStepCompleted?: () => void
  backButtonText: string
  nextButtonText: string
  completeButtonText: string
  /**
   * Passed straight to the Next/Complete button — in practice `disabled`, which
   * is the ONLY thing standing between an incomplete answer and the next step.
   */
  nextButtonProps?: ButtonHTMLAttributes<HTMLButtonElement>
  /** Drawn in place of the steps once the last one is done. */
  completedContent?: ReactNode
  stepCircleContainerClassName?: string
  stepContainerClassName?: string
  contentClassName?: string
  footerClassName?: string
}

export function Stepper({
  children,
  onStepChange,
  onFinalStepCompleted,
  backButtonText,
  nextButtonText,
  completeButtonText,
  nextButtonProps,
  completedContent = null,
  stepCircleContainerClassName = '',
  stepContainerClassName = '',
  contentClassName = '',
  footerClassName = '',
}: StepperProps) {
  const steps = Children.toArray(children)
  const totalSteps = steps.length

  const [currentStep, setCurrentStep] = useState(1)
  const isCompleted = currentStep > totalSteps
  const isLastStep = currentStep === totalSteps

  /**
   * The step actually on screen, which trails `currentStep` by one handover.
   *
   * Two pieces of state rather than one because the outgoing step has to stay
   * mounted long enough to animate away. `currentStep` is the answer to "where
   * are we"; `shown` is the answer to "what is drawn".
   */
  const [shown, setShown] = useState(1)
  const [leaving, setLeaving] = useState(false)
  /** −1 back, 1 forward. Decides which way both halves of the handover slide. */
  const [direction, setDirection] = useState(1)

  useEffect(() => {
    if (shown === currentStep) return
    setLeaving(true)
    const id = window.setTimeout(() => {
      setShown(currentStep)
      setLeaving(false)
    }, OUT_MS)
    return () => {
      window.clearTimeout(id)
    }
  }, [currentStep, shown])

  /**
   * The height of the step on screen, so the card can grow into the next one
   * instead of jumping.
   *
   * Measured rather than guessed, and re-measured on every resize: a validation
   * message appearing under a field changes the height without changing the
   * step. `undefined` until the first measurement — that is what keeps the card
   * `height: auto` on the server, where nothing has measured anything and a
   * fixed height would clip the whole form.
   *
   * KEYED ON `shown`, and that is not a detail. The slide carries a `key`, so a
   * step change REPLACES the element; an observer left on the old one reports
   * the size it has after being detached, which is zero, and the card collapses
   * onto a step that is still in the DOM and no longer on screen. Re-running
   * the effect disconnects the old observer and measures the node that is
   * actually there.
   */
  const slideRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number>()

  useLayoutEffect(() => {
    const el = slideRef.current
    if (!el) return
    const observer = new ResizeObserver(() => {
      // `isConnected` for the same reason: the disconnect below and the
      // observer's own last callback can race, and a measurement of something
      // no longer in the document is never the answer.
      if (el.isConnected) setHeight(el.offsetHeight)
    })
    observer.observe(el)
    setHeight(el.offsetHeight)
    return () => {
      observer.disconnect()
    }
  }, [shown])

  const goTo = (next: number) => {
    setDirection(next > currentStep ? 1 : -1)
    setCurrentStep(next)
    if (next > totalSteps) onFinalStepCompleted?.()
    else onStepChange?.(next)
  }

  return (
    <div className="outer-container">
      <div className={`step-circle-container ${stepCircleContainerClassName}`}>
        {/* Decoration, and hidden as such. What a screen reader needs from this
            row is "which step am I on", and that is one short sentence rather
            than five loose digits — see the live region below. */}
        <div className={`step-indicator-row ${stepContainerClassName}`} aria-hidden="true">
          {steps.map((_, index) => {
            const step = index + 1
            const status =
              currentStep === step ? 'active' : currentStep < step ? 'inactive' : 'complete'
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: the position IS the identity here.
              <Fragment key={index}>
                <span className="step-indicator" data-status={status}>
                  <span className="step-indicator-inner">
                    {status === 'complete' ? (
                      <CheckIcon />
                    ) : status === 'active' ? (
                      <span className="active-dot" />
                    ) : (
                      <span className="step-number">{step}</span>
                    )}
                  </span>
                </span>
                {step < totalSteps ? (
                  <span className="step-connector" data-complete={currentStep > step}>
                    <span className="step-connector-inner" />
                  </span>
                ) : null}
              </Fragment>
            )
          })}
        </div>

        <p className="sr-only" aria-live="polite">
          {isCompleted ? 'Formularz wysłany.' : `Krok ${currentStep} z ${totalSteps}`}
        </p>

        {isCompleted && completedContent ? (
          <div className={`step-content-default ${contentClassName}`}>{completedContent}</div>
        ) : (
          <div
            className={`step-content-default ${contentClassName}`}
            style={height === undefined ? undefined : { height }}
          >
            {/* `key` on the step, so the incoming one MOUNTS — its entrance is a
                CSS animation, which runs on mount and needs no second frame to
                be armed. The outgoing one is the same element with
                `data-leaving`, where a transition is the right tool because it
                is already on screen. */}
            <div
              key={shown}
              ref={slideRef}
              className="step-slide"
              data-leaving={leaving || undefined}
              style={{ '--step-dir': direction >= 0 ? '1' : '-1' } as CSSProperties}
            >
              {steps[shown - 1]}
            </div>
          </div>
        )}

        {isCompleted ? null : (
          <div className={`footer-container ${footerClassName}`}>
            <div className={`footer-nav ${currentStep === 1 ? 'end' : 'spread'}`}>
              {currentStep === 1 ? null : (
                <button type="button" className="back-button" onClick={() => goTo(currentStep - 1)}>
                  {backButtonText}
                </button>
              )}
              <button
                type="button"
                className="next-button"
                onClick={() => goTo(currentStep + 1)}
                {...nextButtonProps}
              >
                {isLastStep ? completeButtonText : nextButtonText}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The tick, drawn rather than faded in.
 *
 * `stroke-dashoffset` animated from the path's own length is the CSS spelling
 * of framer-motion's `pathLength` — the same trick `flou-growth-chart` uses for
 * its line, so the repo has one answer to "animate a stroke" instead of two.
 */
function CheckIcon() {
  return (
    <svg
      className="check-icon"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}
